import {
  getCegjelzoCompanyNameCandidates,
  getCegjelzoTaxNumber,
} from "./formatCegjelzoCompanyDisplay.js";

/** Cégjelző registry `type` values (string or 0–4). */
export const CEGJELZO_REGISTRY_TYPES = {
  COMPANIES: "COMPANIES",
  SELF_EMPLOYED: "SELF_EMPLOYED",
  CIVIL_ORGS: "CIVIL_ORGS",
  BUDGET_ORGS: "BUDGET_ORGS",
  GROUP_TAX_NUMBERS: "GROUP_TAX_NUMBERS",
};

const CEGJELZO_TYPE_BY_NUMBER = {
  0: CEGJELZO_REGISTRY_TYPES.COMPANIES,
  1: CEGJELZO_REGISTRY_TYPES.SELF_EMPLOYED,
  2: CEGJELZO_REGISTRY_TYPES.CIVIL_ORGS,
  3: CEGJELZO_REGISTRY_TYPES.BUDGET_ORGS,
  4: CEGJELZO_REGISTRY_TYPES.GROUP_TAX_NUMBERS,
};

/** EV contact flow — matches provider_business_filter entrepreneur tier slugs. */
export const EV_COMPANY_TYPES = new Set([
  "entrepreneur",
  "singleprimary",
  "singlesecondary",
  "kiva",
  "vszja",
]);

/**
 * Search-flow `yearlyrevenue` bucket values (millions HUF).
 * Aligns with `CompanyRevenue` / `revenueRangesInput` slider bands.
 */
export const COMPANY_YEARLY_REVENUE_BANDS = [
  { maxMillion: 5, value: 5 },
  { maxMillion: 12, value: 10 },
  { maxMillion: 50, value: 50 },
  { maxMillion: 100, value: 100 },
  { maxMillion: 200, value: 200 },
  { maxMillion: 500, value: 500 },
  { maxMillion: 2000, value: 1999 },
  { maxMillion: Infinity, value: 2000 },
];

const LEGAL_FORM_SUFFIX_RULES = [
  { pattern: /\bNyrt\.?\s*$/i, companytype: "nyrt" },
  { pattern: /\bZrt\.?\s*$/i, companytype: "zrt" },
  { pattern: /\bKft\.?\s*$/i, companytype: "kft" },
  { pattern: /\bBt\.?\s*$/i, companytype: "bt" },
  { pattern: /\bKkt\.?\s*$/i, companytype: "kkt" },
  { pattern: /\btársasház\b/i, companytype: "condo" },
];

/** Cégjelző `company_type` / full legal name → search-flow `companytype` slug. */
const HU_LEGAL_FORM_LABEL_RULES = [
  {
    pattern: /korl[aá]tolt\s+felel[oő]ss[eé]g[uű]\s+t[aá]rsas[aá]g/i,
    companytype: "kft",
  },
  { pattern: /bet[eé]ti\s+t[aá]rsas[aá]g/i, companytype: "bt" },
  {
    pattern: /z[aá]rtk[oö]r[uű]en\s+m[uű]k[oö]d[oő]\s+r[eé]szv[eé]nyt[aá]rsas[aá]g/i,
    companytype: "zrt",
  },
  {
    pattern: /nyilv[aá]nosan\s+m[uű]k[oö]d[oő]\s+r[eé]szv[eé]nyt[aá]rsas[aá]g/i,
    companytype: "nyrt",
  },
  { pattern: /k[oö]zkereseti\s+t[aá]rsas[aá]g/i, companytype: "kkt" },
  { pattern: /t[aá]rsash[aá]z/i, companytype: "condo" },
];

const pickCegjelzoScalarString = (value) => {
  if (typeof value === "string" && value.trim()) {
    return value.trim();
  }
  if (Array.isArray(value) && value.length > 0) {
    return pickCegjelzoScalarString(value[0]);
  }
  if (value && typeof value === "object") {
    if (typeof value.company_type === "string" && value.company_type.trim()) {
      return value.company_type.trim();
    }
    if (typeof value.name === "string" && value.name.trim()) {
      return value.name.trim();
    }
  }
  return "";
};

const extractCegjelzoApiCompanyTypeLabel = (companyData) => {
  if (!companyData || typeof companyData !== "object") return "";

  return (
    pickCegjelzoScalarString(companyData.company_type) ||
    pickCegjelzoScalarString(companyData.company_form) ||
    pickCegjelzoScalarString(companyData.general_details)
  );
};

export const mapHuLegalFormLabelToCompanyType = (label) => {
  const trimmed = typeof label === "string" ? label.trim() : "";
  if (!trimmed) return undefined;

  for (const rule of HU_LEGAL_FORM_LABEL_RULES) {
    if (rule.pattern.test(trimmed)) {
      return rule.companytype;
    }
  }

  return undefined;
};

export const normalizeCegjelzoRegistryType = (rawType) => {
  if (typeof rawType === "string" && rawType.trim().length > 0) {
    return rawType.trim().toUpperCase();
  }
  const numeric = Number(rawType);
  if (Number.isFinite(numeric) && CEGJELZO_TYPE_BY_NUMBER[numeric]) {
    return CEGJELZO_TYPE_BY_NUMBER[numeric];
  }
  return typeof rawType === "string" ? rawType.trim().toUpperCase() : "";
};

export const isCegjelzoCompaniesRegistryType = (companyData) =>
  normalizeCegjelzoRegistryType(companyData?.type) ===
  CEGJELZO_REGISTRY_TYPES.COMPANIES;

/**
 * Parse our `companytype` slug from a registered company name suffix.
 * Returns undefined when the legal form cannot be determined reliably.
 */
export const parseCompanyTypeFromLegalName = (name) => {
  const trimmed = typeof name === "string" ? name.trim() : "";
  if (!trimmed) return undefined;

  for (const rule of LEGAL_FORM_SUFFIX_RULES) {
    if (rule.pattern.test(trimmed)) {
      return rule.companytype;
    }
  }

  return mapHuLegalFormLabelToCompanyType(trimmed);
};

/**
 * COMPANIES → companytype slug.
 * 1) Cégjelző `company_type` API field (Hungarian legal-form label).
 * 2) Name-based parse via `getCegjelzoCompanyNameCandidates` (short_name → name → long_name).
 */
const mapCompaniesRegistryTypeToCompanyType = (companyData) => {
  const fromApiLabel = mapHuLegalFormLabelToCompanyType(
    extractCegjelzoApiCompanyTypeLabel(companyData),
  );
  if (fromApiLabel) {
    return fromApiLabel;
  }

  for (const name of getCegjelzoCompanyNameCandidates(companyData)) {
    const fromName = parseCompanyTypeFromLegalName(name);
    if (fromName) {
      return fromName;
    }
  }

  return undefined;
};

export const isEvCompanyType = (companytype) =>
  EV_COMPANY_TYPES.has(companytype);

export const isCompanyContactCompanyType = (companytype) =>
  !!companytype && !isEvCompanyType(companytype);

/**
 * Whether the user may select this Cégjelző registry row in auto-fill.
 * All registry categories are allowed except GROUP_TAX_NUMBERS.
 */
export const isCegjelzoRegistryTypeAutoFillSupported = (registryType) => {
  const normalized = normalizeCegjelzoRegistryType(registryType);
  if (!normalized) return false;
  return normalized !== CEGJELZO_REGISTRY_TYPES.GROUP_TAX_NUMBERS;
};

/**
 * Latest `revenue[].netto_income` from a Cégjelző company payload (HUF).
 */
export const extractCegjelzoNettoIncomeHuf = (companyData) => {
  const revenueRows = companyData?.revenue;
  if (!Array.isArray(revenueRows) || revenueRows.length === 0) {
    return undefined;
  }

  const withIncome = revenueRows.filter((row) => {
    const raw = row?.netto_income;
    return raw !== null && raw !== undefined && raw !== "";
  });
  if (withIncome.length === 0) {
    return undefined;
  }

  const sorted = [...withIncome].sort((a, b) => {
    const endA = typeof a.period_end === "string" ? a.period_end : "";
    const endB = typeof b.period_end === "string" ? b.period_end : "";
    return endB.localeCompare(endA);
  });

  const numeric = Number(sorted[0].netto_income);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : undefined;
};

/**
 * Map net annual revenue (HUF) → search-flow `yearlyrevenue` bucket (millions HUF).
 */
export const mapNettoIncomeHufToYearlyRevenueBand = (nettoIncomeHuf) => {
  const millionFt = Number(nettoIncomeHuf) / 1_000_000;
  if (!Number.isFinite(millionFt) || millionFt < 0) {
    return undefined;
  }

  for (const band of COMPANY_YEARLY_REVENUE_BANDS) {
    if (millionFt <= band.maxMillion) {
      return band.value;
    }
  }

  return COMPANY_YEARLY_REVENUE_BANDS[COMPANY_YEARLY_REVENUE_BANDS.length - 1]
    .value;
};

/**
 * Cégjelző `revenue[].netto_income` → search-flow `yearlyrevenue` band.
 * COMPANIES only; returns `undefined` when revenue is missing.
 */
export const mapCegjelzoCompanyToYearlyRevenue = (companyData) => {
  if (!isCegjelzoCompaniesRegistryType(companyData)) {
    return undefined;
  }

  const nettoIncomeHuf = extractCegjelzoNettoIncomeHuf(companyData);
  if (nettoIncomeHuf === undefined) {
    return undefined;
  }

  return mapNettoIncomeHufToYearlyRevenueBand(nettoIncomeHuf);
};

/**
 * Cégjelző `fin_head_count.head_count` → search-flow `noofemployees`.
 * Only registered companies (COMPANIES) return a value; missing/invalid headcount → `0`.
 * Non-COMPANIES registry types return `undefined` (caller resets stale state).
 */
export const mapCegjelzoCompanyToNoOfEmployees = (companyData) => {
  if (!companyData || typeof companyData !== "object") return undefined;

  if (!isCegjelzoCompaniesRegistryType(companyData)) return undefined;

  const rawHeadCount = companyData.fin_head_count?.head_count;
  if (rawHeadCount === null || rawHeadCount === undefined || rawHeadCount === "") {
    return 0;
  }

  const numeric = Number(rawHeadCount);
  return Number.isFinite(numeric) ? numeric : 0;
};

/**
 * Hungarian adószám layout: NNNNNNNN-Y-ZZ (11 digits).
 * Y (9th digit) = NAV áfakód → search-flow `vatFilter`:
 *   1 = alanyi adómentes / adómentes tevékenység → 'false'
 *   2 = általános áfás
 *   3 = EVA (legacy, megszűnt 2020), 
 *   4 = csoportos adóalany → 'true'
 *   5 = csoport közös adószám — not mapped
 */
const HUNGARIAN_TAX_VAT_FILTER_FALSE_DIGITS = new Set([1]);
const HUNGARIAN_TAX_VAT_FILTER_TRUE_DIGITS = new Set([2, 3, 4]);
const parseHungarianTaxNumberStatusDigit = (taxNumberRaw) => {
  const digits = String(taxNumberRaw ?? "").replace(/\D/g, "");
  if (digits.length < 9) {
    return undefined;
  }

  const statusDigit = Number(digits.charAt(8));
  return Number.isFinite(statusDigit) ? statusDigit : undefined;
};

/**
 * Map adószám 9th digit → `vatFilter` ('true' | 'false').
 * Requires at least 9 digits (8-digit adótörzs alone is not enough).
 */
export const mapHungarianTaxNumberToVatFilter = (taxNumberRaw) => {
  const statusDigit = parseHungarianTaxNumberStatusDigit(taxNumberRaw);
  if (statusDigit === undefined) {
    return undefined;
  }

  if (HUNGARIAN_TAX_VAT_FILTER_FALSE_DIGITS.has(statusDigit)) {
    return "false";
  }

  if (HUNGARIAN_TAX_VAT_FILTER_TRUE_DIGITS.has(statusDigit)) {
    return "true";
  }

  return undefined;
};

/** Cégjelző company payload → `vatFilter` from `full_tax_number` / `tax_number`. */
export const mapCegjelzoCompanyToVatFilter = (companyData) => {
  if (!companyData || typeof companyData !== "object") {
    return undefined;
  }

  const taxNumber = getCegjelzoTaxNumber(companyData);
  if (!taxNumber) {
    return undefined;
  }

  return mapHungarianTaxNumberToVatFilter(taxNumber);
};

/**
 * Map Cégjelző payload → search-flow `companytype` slug.
 *
 * COMPANIES: name suffix (kft, bt, zrt, …)
 * SELF_EMPLOYED: entrepreneur (tax/employment steps refine later)
 * CIVIL_ORGS: civil
 * BUDGET_ORGS: citycouncil
 * GROUP_TAX_NUMBERS / unknown: undefined
 */

export const mapCegjelzoCompanyToCompanyType = (companyData) => {
  if (!companyData || typeof companyData !== "object") return undefined;

  const registryType =
    normalizeCegjelzoRegistryType(companyData.type) ||
    normalizeCegjelzoRegistryType(companyData.collection);

  switch (registryType) {
    case CEGJELZO_REGISTRY_TYPES.SELF_EMPLOYED:
      return "entrepreneur";
    case CEGJELZO_REGISTRY_TYPES.CIVIL_ORGS:
      return "civil";
    case CEGJELZO_REGISTRY_TYPES.BUDGET_ORGS:
      return "citycouncil";
    case CEGJELZO_REGISTRY_TYPES.COMPANIES:
      return mapCompaniesRegistryTypeToCompanyType(companyData);
    default:
      return undefined;
  }
};
